import { CircleCheck, Download, Share, SquarePlus } from '@orgus/ui/icons';
import { Button, toast } from '@orgus/ui';
import { detectPlatform, promptInstall, usePwa } from './pwa.ts';

/** Install onboarding. iOS needs Add to Home Screen before it offers push, so it gets the steps. */
export function InstallCard() {
  const standalone = usePwa((s) => s.standalone);
  const canPrompt = usePwa((s) => !!s.installPrompt);
  const platform = detectPlatform(navigator.userAgent, navigator.maxTouchPoints);

  if (standalone) {
    return (
      <div className="flex items-start gap-3">
        <CircleCheck className="mt-1 size-4 text-passed" aria-hidden />
        <div>
          <p className="text-ui font-medium text-fg">Installed</p>
          <p className="text-ui text-fg-muted">Orgus runs as an app on this device.</p>
        </div>
      </div>
    );
  }

  if (platform === 'ios') {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-ui font-medium text-fg">Install Orgus on this iPhone or iPad</p>
        <p className="text-ui text-fg-muted">iOS only sends notifications to apps on the Home Screen. In Safari:</p>
        <ol className="flex flex-col gap-2 text-ui text-fg">
          <li className="flex items-center gap-3">
            <span className="flex size-7 shrink-0 items-center justify-center rounded-control border border-border bg-raised text-caption num">1</span>
            <span>
              Tap Share <Share className="inline size-4 align-text-bottom text-accent" aria-label="the Share icon" />
            </span>
          </li>
          <li className="flex items-center gap-3">
            <span className="flex size-7 shrink-0 items-center justify-center rounded-control border border-border bg-raised text-caption num">2</span>
            <span>
              Choose Add to Home Screen <SquarePlus className="inline size-4 align-text-bottom text-fg-muted" aria-hidden />
            </span>
          </li>
          <li className="flex items-center gap-3">
            <span className="flex size-7 shrink-0 items-center justify-center rounded-control border border-border bg-raised text-caption num">3</span>
            <span>Open Orgus from the Home Screen and turn notifications on</span>
          </li>
        </ol>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-ui font-medium text-fg">Install Orgus on this device</p>
      <p className="text-ui text-fg-muted">
        It opens full screen from the {platform === 'android' ? 'home screen' : 'dock or start menu'}, keeps the last snapshot for offline viewing, and gets notifications.
      </p>
      {canPrompt ? (
        <Button
          variant="primary"
          size="lg"
          className="h-11"
          icon={<Download />}
          onClick={() =>
            void promptInstall().then((r) => {
              if (r === 'accepted') toast.success('Orgus is installed');
            })
          }
        >
          Install app
        </Button>
      ) : (
        <p className="text-caption text-fg-subtle">Use the browser menu: Install app, or Add to Home screen.</p>
      )}
    </div>
  );
}

/** Why the phone cannot reach the hub, and the two ways to fix it. */
export function PhoneAccessHelp() {
  const secure = window.isSecureContext;
  return (
    <div className="flex flex-col gap-2 text-ui text-fg-muted">
      <p>
        Install and push need https. {secure ? 'This address is secure.' : `This address (${window.location.origin}) is not, so install and push are off here.`}
      </p>
      <p>
        <span className="font-medium text-fg">Tailscale (recommended):</span> on the hub machine run <code className="rounded bg-bg px-1 font-mono text-caption">tailscale serve --bg 4600</code>, start the
        hub with <code className="rounded bg-bg px-1 font-mono text-caption">ORGUS_ALLOWED_HOSTS=.ts.net</code> and <code className="rounded bg-bg px-1 font-mono text-caption">ORGUS_TRUSTED_PROXIES=127.0.0.1,::1</code>, then open the https address Tailscale prints on your phone, with Tailscale on.
      </p>
      <p>
        <span className="font-medium text-fg">Your own https proxy:</span> Caddy or nginx with a real certificate in front of the hub, its name in ORGUS_ALLOWED_HOSTS, forwarding
        X-Forwarded-Proto. On the hub computer it also needs ORGUS_TRUSTED_PROXIES=127.0.0.1,::1, or sign-in through it is refused.
      </p>
      <p>Sign in on the phone with a link from <code className="rounded bg-bg px-1 font-mono text-caption">orgus token</code>, with the https address in place of 127.0.0.1.</p>
    </div>
  );
}
